import { useMemo } from 'react';
import { graphql, useSubscription } from 'react-relay';

import type { GraphQLSubscriptionConfig } from 'relay-runtime';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { usePublishIssueEvent } from '../common/utils/issue-pub-sub/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../common/constants.tsx';
import { ISSUE_EVENT_NAMES } from '../common/utils/issue-pub-sub/types.tsx';
import type { useListSubscriptions_issueDeletedSubscription as IssueDeletedSubscription } from './__generated__/useListSubscriptions_issueDeletedSubscription.graphql';
import type { useListSubscriptions_issueUpdatedSubscription as IssueUpdatedSubscription } from './__generated__/useListSubscriptions_issueUpdatedSubscription.graphql';
import type { useListSubscriptions_issueCreatedSubscription as IssueCreatedSubscription } from './__generated__/useListSubscriptions_issueCreatedSubscription.graphql';

const CREATE_ERROR_ID = 'realTimeIssueCreate';
const UPDATE_ERROR_ID = 'realTimeIssueUpdate';
const DELETE_ERROR_ID = 'realTimeIssueDelete';

type Props = {
	projectIds: string[];
};

export const issueCreatedSubscription = graphql`
	subscription useListSubscriptions_issueCreatedSubscription(
		$cloudId: ID!
		$projectIds: [String!]
	) {
		jira {
			onIssueCreatedByProjectsNoEnrichment(cloudId: $cloudId, projectIds: $projectIds) {
				resource
			}
		}
	}
`;

export const issueUpdatedSubscription = graphql`
	subscription useListSubscriptions_issueUpdatedSubscription(
		$cloudId: ID!
		$projectIds: [String!]
	) {
		jira {
			onIssueUpdatedByProjectsNoEnrichment(cloudId: $cloudId, projectIds: $projectIds) {
				resource
			}
		}
	}
`;

export const issueDeletedSubscription = graphql`
	subscription useListSubscriptions_issueDeletedSubscription(
		$cloudId: ID!
		$projectIds: [String!]
	) {
		jira {
			onIssueDeletedByProjectsNoEnrichment(cloudId: $cloudId, projectIds: $projectIds) {
				resource
			}
		}
	}
`;

export function useListSubscriptions({ projectIds }: Props) {
	const cloudId = useCloudId();

	const variables = useMemo(
		() => ({
			cloudId,
			projectIds,
		}),
		[cloudId, projectIds],
	);

	const publishIssueEvent = usePublishIssueEvent();

	const createConfig = useMemo<GraphQLSubscriptionConfig<IssueCreatedSubscription>>(
		() => ({
			variables,
			subscription: issueCreatedSubscription,
			onNext: async (response) => {
				try {
					const resourceId = response?.jira?.onIssueCreatedByProjectsNoEnrichment?.resource;
					if (resourceId) {
						publishIssueEvent(ISSUE_EVENT_NAMES.UPDATED_OR_CREATED, { resourceId });
					}
				} catch (error) {
					fireErrorAnalytics({
						meta: {
							id: CREATE_ERROR_ID,
							packageName: PACKAGE_NAME,
							teamName: TEAM_NAME,
						},
						attributes: { logLocation: 'onNext' },
						error: error instanceof Error ? error : undefined,
						sendToPrivacyUnsafeSplunk: true,
					});
				}
			},
			onError: (error) => {
				fireErrorAnalytics({
					meta: {
						id: CREATE_ERROR_ID,
						packageName: PACKAGE_NAME,
						teamName: TEAM_NAME,
					},
					attributes: { logLocation: 'onError' },
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
			},
		}),
		[variables, publishIssueEvent],
	);

	const updateConfig = useMemo<GraphQLSubscriptionConfig<IssueUpdatedSubscription>>(
		() => ({
			variables,
			subscription: issueUpdatedSubscription,
			onNext: async (response) => {
				try {
					const resourceId = response?.jira?.onIssueUpdatedByProjectsNoEnrichment?.resource;
					if (resourceId) {
						publishIssueEvent(ISSUE_EVENT_NAMES.UPDATED_OR_CREATED, { resourceId });
					}
				} catch (error) {
					fireErrorAnalytics({
						meta: {
							id: UPDATE_ERROR_ID,
							packageName: PACKAGE_NAME,
							teamName: TEAM_NAME,
						},
						attributes: { logLocation: 'onNext' },
						error: error instanceof Error ? error : undefined,
						sendToPrivacyUnsafeSplunk: true,
					});
				}
			},
			onError: (error) => {
				fireErrorAnalytics({
					meta: {
						id: UPDATE_ERROR_ID,
						packageName: PACKAGE_NAME,
						teamName: TEAM_NAME,
					},
					attributes: { logLocation: 'onError' },
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
			},
		}),
		[variables, publishIssueEvent],
	);
	const deleteConfig = useMemo<GraphQLSubscriptionConfig<IssueDeletedSubscription>>(() => {
		return {
			variables,
			subscription: issueDeletedSubscription,
			updater: (store, response) => {
				try {
					if (response?.jira?.onIssueDeletedByProjectsNoEnrichment?.resource) {
						publishIssueEvent(ISSUE_EVENT_NAMES.DELETED, {
							resourceId: response.jira.onIssueDeletedByProjectsNoEnrichment.resource,
							store,
						});
					}
				} catch (error) {
					fireErrorAnalytics({
						meta: {
							id: DELETE_ERROR_ID,
							packageName: PACKAGE_NAME,
							teamName: TEAM_NAME,
						},
						attributes: { logLocation: 'updater' },
						error: error instanceof Error ? error : undefined,
						sendToPrivacyUnsafeSplunk: true,
					});
				}
			},
			onError: (error) => {
				fireErrorAnalytics({
					meta: {
						id: DELETE_ERROR_ID,
						packageName: PACKAGE_NAME,
						teamName: TEAM_NAME,
					},
					attributes: { logLocation: 'onError' },
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
			},
		};
	}, [variables, publishIssueEvent]);

	useSubscription(createConfig);
	useSubscription(updateConfig);
	useSubscription(deleteConfig);

	return null;
}
